import React, { useState, useRef, useEffect } from "react";

type Tab = {
  label: string;
  value: string;
  children: React.ReactNode;
};

interface TabsProps {
  defaultValue?: string;
  tabs: Tab[];
}

export default function Tabs({ defaultValue, tabs }: TabsProps) {
  const initialTab =
    defaultValue && tabs.some((tab) => tab.value === defaultValue)
      ? defaultValue
      : tabs[0]?.value;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Check if overflow exists and update arrow visibility
  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Check overflow on mount and tab changes
  useEffect(() => {
    checkOverflow();
    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [tabs]);

  return (
    <div>
      {/* Tab Navigation with Carousel */}
      <div style={{ position: "relative", marginBottom: "1.5rem" }}>
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={scrollLeft}
            style={{
              position: "absolute",
              left: "8px",
              top: "50%",
              transform: "translateY(-60%)",
              height: "32px",
              width: "32px",
              background: "#0284c7",
              border: "none",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              color: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            ‹
          </button>
        )}

        {/* Scrollable Tab Container */}
        <div
          ref={scrollContainerRef}
          onScroll={checkOverflow}
          style={{
            display: "flex",
            borderBottom: "2px solid #e2e8f0",
            overflowX: "auto",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE
            paddingLeft: showLeftArrow ? "48px" : "0",
            paddingRight: showRightArrow ? "48px" : "0",
            scrollBehavior: "smooth",
          }}
          onLoad={checkOverflow}
        >
          <style>
            {`
              div::-webkit-scrollbar {
                display: none; /* Chrome, Safari */
              }
            `}
          </style>
          {tabs.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setActiveTab(value)}
              style={{
                padding: "0.75rem 1.25rem",
                background: "none",
                border: "none",
                borderBottom:
                  activeTab === value
                    ? "5px solid #0284c7"
                    : "5px solid transparent",
                color: activeTab === value ? "#0284c7" : "inherit",
                fontWeight: activeTab === value ? "700" : "500",
                cursor: "pointer",
                flexShrink: 0,
                whiteSpace: "nowrap",
                transition: "all 0.2s ease",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={scrollRight}
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-60%)",
              height: "32px",
              width: "32px",
              background: "#0284c7",
              border: "none",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              color: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            ›
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div>{tabs.find((tab) => tab.value === activeTab)?.children}</div>
    </div>
  );
}
